import styles from './Step.module.css';

import classNames from 'classnames';

import TrashIcon from '../../assets/icons/trash.svg?react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FC, useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';

import { CommonApi, ReceiptApi } from '../../api';
import { AddressResponse } from '../../model/Address';
import { formatMedicinePlural, formatMoney } from '../../utils';
import { CheckMessagesBlock } from '../molecules/CheckMessagesBlock';
import { ConfirmOrderModal } from './ConfirmOrderModal';
import { CreateAddressModal } from './CreateAddressModal';
import {
  DoctorAddMedicineToOrderForm,
  OrderMedicine,
} from './DoctorAddMedicineToOrderForm';

interface Step1Props {
  onPrev: () => void;
  formData: any;
  userId: number;
}

interface FormFields {
  address: AddressResponse;
  description: string;
}

export const Step1: FC<Step1Props> = ({
  onPrev: _onPrev,
  formData: _formData,
  userId,
}) => {
  const [receiptId, setReceiptId] = useState<number | undefined>();
  const [isCheckSuccessful, setIsCheckSuccessful] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [checkMessages, setCheckMessages] = useState<
    { message: string; level: 'info' | 'warning' | 'danger' }[]
  >([]);

  const { mutate: checkMutate, isLoading: isChecking } = useMutation({
    mutationFn: ReceiptApi.check,
    onSuccess(response) {
      console.log(response);
      setCheckMessages(response);
      setIsCheckSuccessful(true);
    },
  });
  const { mutate: creationMutate, isLoading: isCreating } = useMutation({
    mutationFn: ReceiptApi.clientCreate,
    onSuccess(response) {
      setReceiptId(response.receipt_id);
    },
  });
  const [medicineList, setMedicineList] = useState<OrderMedicine[]>([]);

  const { data: paymentList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 206],
    queryFn: () => CommonApi.getReference(206),
  });

  useQuery({
    queryKey: [CommonApi.KEY, 'getRefHeaders'],
    queryFn: CommonApi.getRefHeaders,
  });

  const { control, getValues, reset, trigger } = useForm<FormFields>();

  const closeConfirmOrderModal = useCallback(() => {
    setReceiptId(undefined);
    reset();
  }, [reset]);

  const checkOrder = useCallback(async () => {
    const [description] = getValues(['description']);
    checkMutate({
      description,
      receipt_items: medicineList.flatMap((medicine) =>
        medicine.time.map((time) => ({
          total_amount: medicine.totalCount,
          amount: Number(time.count),
          duration: medicine.duration,
          start: medicine.start,
          daytime_name: time.value,
          frequency_name: medicine.frequency.code,
          eating_name: time.value,
          medicine_name: medicine.medicine.name,
        })),
      ),
    });
  }, [getValues, medicineList, checkMutate]);

  const createOrder = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    const [description, address] = getValues(['description', 'address']);
    const paymentMethodId =
      paymentList.find((p) => /юкасса|yookassa/i.test(p.value))?.id ??
      paymentList[0]?.id;
    if (!paymentMethodId) {
      return;
    }
    creationMutate({
      description,
      patient_id: userId,
      address: address.id,
      payment_method: paymentMethodId,
      analysis_result: checkMessages,
      recipient: {
        surname: '',
        name: '',
      },
      receipt_items: medicineList.flatMap((medicine) =>
        medicine.time.map((time) => ({
          total_amount: medicine.totalCount,
          amount: Number(time.count),
          duration: medicine.duration,
          start: medicine.start,
          daytime_id: time.timeId,
          frequency_id: medicine.frequency.id,
          eating_id: time.eatingId,
          medicine_id: medicine.medicine.id,
        })),
      ),
    });
  }, [
    getValues,
    medicineList,
    creationMutate,
    trigger,
    checkMessages,
    userId,
    paymentList,
  ]);
  return (
    <div>
      <ConfirmOrderModal
        receiptId={receiptId}
        address={getValues('address')?.id}
        onClose={closeConfirmOrderModal}
        userRole="landing_user"
      />
      <div className={styles.gridStep1Container}>
        <div
          className={classNames(styles.column, 'text-brand-1  rounded-l-lg')}
        >
          <header className="rounded-tl-lg bg-brand-6 px-[24px] py-[16px]">
            <h2 className="text-[20px] font-bold leading-normal">Детали</h2>
          </header>
          <div className="flex gap-[12px] border-b border-dashed border-brand-1/50 p-[24px]">
            <Controller
              control={control}
              name="address"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <div className="mb-[12px] flex w-full items-center gap-[8px]">
                  <input
                    type="text"
                    readOnly
                    className={classNames(
                      'input',
                      !field.value
                        ? 'text-brand-1/40 border-brand-2'
                        : 'text-black',
                    )}
                    value={field.value?.address_text || 'Выберите адрес'}
                    onClick={() => setIsModalOpen(true)}
                  />
                  <CreateAddressModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    patientId={userId}
                    initialData={field.value}
                    onAddressSelect={(address) => {
                      field.onChange(address);
                      setIsModalOpen(false);
                    }}
                  />
                </div>
              )}
            />
          </div>
          <DoctorAddMedicineToOrderForm
            onAdd={(medicine) =>
              setMedicineList((current) => current.concat([medicine]))
            }
          />
        </div>
        <div className={classNames(styles.column, 'rounded-r-lg text-brand-1')}>
          <div className="grow">
            <header className="rounded-tr-lg bg-brand-6 px-[24px] py-[16px]">
              <h2 className="text-[20px] font-bold leading-normal">Заказ</h2>
            </header>
            <div className="border-b-4 border-brand-3 px-[24px] pb-[24px] pt-[12px]">
              <table className="table">
                <thead>
                  <tr>
                    <th className="w-[52px]">&nbsp;</th>
                    <th className="text-left">Лекарство</th>
                    <th className="text-left">Частота приёма</th>
                    <th className="text-left">Цена</th>
                  </tr>
                </thead>
                <tbody>
                  {medicineList.length === 0 && (
                    <tr>
                      <td className="text-center" colSpan={4}>
                        Пусто
                      </td>
                    </tr>
                  )}
                  {medicineList.map(
                    (
                      {
                        totalCount,
                        count,
                        frequency,
                        medicine,
                        time,
                        duration,
                      },
                      index,
                    ) => (
                      <tr className="leading-[24px]" key={index}>
                        <td className="w-[52px]">
                          <button
                            className="inline-flex size-[32px] items-center justify-center rounded-[12px] bg-brand-3"
                            onClick={() =>
                              setMedicineList((current) =>
                                current.filter(
                                  (_, medicineIndex) => medicineIndex !== index,
                                ),
                              )
                            }
                            type="button"
                          >
                            <TrashIcon />
                          </button>
                        </td>
                        <td>
                          <strong className="block">{medicine.name}</strong>
                          <span className="text-brand-1/40">x{totalCount}</span>
                        </td>
                        <td>
                          <span className="block">
                            {totalCount} {formatMedicinePlural(totalCount)}
                            &nbsp;{frequency.value}
                          </span>
                          <span className="text-brand-1/40">
                            {time.map((t) => `${t.value}`).join(', ')}
                          </span>
                        </td>
                        <td>
                          {formatMoney(
                            medicine.medicine_price * count * duration,
                          )}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
              {checkMessages.length > 0 && (
                <CheckMessagesBlock
                  messages={checkMessages}
                  className="mt-[16px]"
                />
              )}
            </div>
            {medicineList.length > 0 && (
              <Controller
                control={control}
                name="description"
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <label className="block w-full p-[24px]">
                    <span className="mb-[12px] block">
                      Комментарий к заказу
                    </span>
                    <TextareaAutosize
                      {...field}
                      minRows={3}
                      maxRows={10}
                      className={classNames('input w-full resize-none', {
                        'input--invalid': fieldState.error,
                      })}
                      placeholder="Сообщение"
                    />
                  </label>
                )}
              />
            )}
          </div>
          <footer className="flex justify-end border-t border-dashed border-brand-1/50 p-[24px]">
            <button
              className="button button--success px-[32px]"
              disabled={medicineList.length === 0 || isChecking || isCreating}
              onClick={() => {
                if (isCheckSuccessful) {
                  createOrder();
                  setIsCheckSuccessful(false);
                } else {
                  checkOrder();
                }
              }}
              type="button"
            >
              {isChecking || isCreating ? (
                <>Загрузка...</>
              ) : isCheckSuccessful ? (
                'Подтвердить заказ'
              ) : (
                'Проверить заказ'
              )}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};
